import React from "react";
import styles from "./support-chat-modal.module.scss";
import userIconBlue from "@src/assets/svg/user-icon-blue.svg";
import chatBotProfile from "@src/assets/svg/chat-bot-profile.svg";
import attachIcon from "@src/assets/svg/attach-icon.svg";
import thumbsDownOutlineIcon from "@src/assets/svg/thumbs-down-icon-outline-blue.svg";
import thumbsUpOutlineIcon from "@src/assets/svg/thumbs-up-icon-outline-blue.svg";
import thumbsDownRedIcon from "@src/assets/svg/thumbs-down-icon-solid-red.svg";
import thumbsUpGreenIcon from "@src/assets/svg/thumbs-up-icon-solid-green.svg";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { TicketCommentAttachment, TicketCommentItem } from "@src/types/support";
import { isImageFile } from "@src/cloud-connect/support/utils/is-image-file";
import cn from "classnames";
import InitialsAvatar from "@src/components/atoms/initials-avatar/initials-avatar";
import { SmallSpinner } from "@src/components/atoms/small-spinner/small-spinner";
import DOMPurify from "dompurify";
import {
  downloadLocalFile,
  downloadSupportAttachment,
} from "@src/cloud-connect/support/utils/download-from-support-service";
import { AuthImage } from "@src/components/atoms/auth-image/auth-image";
import { supportUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import { useI18N } from "@src/language/language-context";
import { detectHtmlInComment } from "@src/cloud-connect/support/utils/detect-html-in-comment";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { ToolActivitySection } from "@src/components/molecules/tool-activity-section/tool-activity-section";
import { postMessageFeedbackUseCase } from "@src/cloud-connect/rag/use-cases/post-message-feedback-use-case";
import { isSupportHandoverMessage } from "@src/cloud-connect/support/utils/is-support-handover-message";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import {
  StreamTool,
  normalizeHistoricalToolCalls,
} from "@src/components/molecules/tool-activity-section/stream-tools";

const cssClassName = "support-chat-modal";

const normalizeHtmlContent = (content: string): string => {
  let normalized = content;

  normalized = normalized
    .replace(/\\u003c/gi, "<")
    .replace(/\\u003e/gi, ">")
    .replace(/\\u0026/gi, "&")
    .replace(/\\u0022/gi, '"')
    .replace(/\\u0027/gi, "'")
    .replace(/\\"/g, '"')
    .replace(/\\n/g, "\n");

  const trimmed = normalized.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    try {
      normalized = JSON.parse(trimmed);
    } catch {
      // keep original if parsing fails
    }
  }

  if (/%3c|%3e|%26/i.test(normalized)) {
    try {
      normalized = decodeURIComponent(normalized);
    } catch {
      // keep original if decoding fails
    }
  }

  for (let i = 0; i < 5; i += 1) {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = normalized;
    const next = textarea.value;
    if (next === normalized) break;
    normalized = next;
  }

  return normalized;
};

interface ChatMessageProps {
  comment: TicketCommentItem;
  userId: string;
  namespace: string | null;
  onFeedbackChange: (
    namespace: string,
    messageId: string,
    useful: boolean,
  ) => void;
  isThinking?: boolean;
  tools?: StreamTool[];
}

const LocalAttachmentImage = ({
  file,
  className,
  alt,
  title,
}: {
  file: File;
  className: string;
  alt: string;
  title: string;
}) => {
  const [src, setSrc] = React.useState<string>();

  React.useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return src ? (
    <img src={src} className={className} alt={alt} title={title} />
  ) : null;
};

const ThinkingInline = ({
  label,
  withTools,
}: {
  label: string;
  withTools?: boolean;
}) => (
  <div
    className={cn(
      styles[`${cssClassName}__thinking-inline`],
      withTools && styles[`${cssClassName}__thinking-inline--with-tools`],
    )}
  >
    <div className={styles[`${cssClassName}__small-spinner-wrapper`]}>
      <SmallSpinner />
    </div>
    <span>{label}</span>
  </div>
);

const ChatMessageComponent: React.FC<ChatMessageProps> = ({
  comment,
  userId,
  namespace,
  onFeedbackChange,
  isThinking,
  tools,
}) => {
  const i18n = useI18N();
  const [useful, setUseful] = React.useState(comment.useful);
  const [isFeedbackSubmitting, setIsFeedbackSubmitting] = React.useState(false);
  React.useEffect(() => {
    setUseful(comment.useful);
  }, [comment.useful]);

  const isCurrentUserSameAsChatUser = userId === comment.author.id;
  const role = isCurrentUserSameAsChatUser ? "current-users" : "other-users";
  const hasTimestamp = Boolean(comment.created_at);
  const displayTools =
    tools && tools.length > 0
      ? tools
      : comment.tool_calls
      ? normalizeHistoricalToolCalls(comment.tool_calls)
      : [];
  const hasTools = displayTools.length > 0;

  const texts = {
    thinkingBubbleText: i18n("modules.supportChatModal.thinkingBubbleText"),
    errorSendingFeedback: i18n("modules.aiRagChat.errorSendingFeedback"),
    downloadAttachmentError: i18n(
      "modules.supportChat.downloadAttachmentError",
    ),
  };

  const handleAttachmentDownload = async (
    attachment: TicketCommentAttachment,
  ) => {
    if (attachment.local_file) {
      await downloadLocalFile(
        attachment.local_file,
        attachment.file_name,
        texts.downloadAttachmentError,
      );
      return;
    }

    await downloadSupportAttachment(attachment, {
      downloadAttachmentError: i18n(
        "modules.supportChat.downloadAttachmentError",
      ),
      downloadEmpty: i18n("modules.supportChat.downloadEmpty"),
    });
  };

  const content = normalizeHtmlContent(comment.content);
  const isHtml = detectHtmlInComment(content);
  const canGiveFeedback =
    comment.is_ai_chatbot_comment === true &&
    Boolean(namespace) &&
    Boolean(comment.message_id?.trim()) &&
    Boolean(content.trim()) &&
    !isSupportHandoverMessage(content) &&
    !isThinking;

  const handleFeedback = async (useful: boolean) => {
    const messageId = comment.message_id;
    if (!namespace || !messageId?.trim() || isFeedbackSubmitting) return;

    setIsFeedbackSubmitting(true);
    const response = await postMessageFeedbackUseCase(
      namespace,
      messageId,
      useful,
    );
    if (response?.status === 200) {
      setUseful(useful);
      onFeedbackChange(namespace, messageId, useful);
    } else {
      errorSnackbar(texts.errorSendingFeedback);
    }
    setIsFeedbackSubmitting(false);
  };

  // Images (jpg/jpeg/png/webp) are the ones uploaded through the image-picker
  // flow and sent to the RAG agent — they render as thumbnails. Everything
  // else uses the regular attachment download button.
  const isInlineImage = (attachment: TicketCommentAttachment): boolean =>
    isImageFile(attachment.file_name, attachment.content_type);
  const fileAttachments =
    comment?.attachments?.filter((attachment) => !isInlineImage(attachment)) ??
    [];
  const imageAttachments = comment?.attachments?.filter(isInlineImage) ?? [];

  return (
    <div
      className={cn(
        styles[`${cssClassName}__message`],
        styles[`${cssClassName}__message--${role}`],
      )}
    >
      <div
        className={cn(
          styles[`${cssClassName}__message-content`],
          styles[`${cssClassName}__message-content--${role}`],
          hasTimestamp &&
            styles[`${cssClassName}__message-content--with-timestamp`],
        )}
      >
        <div className={styles[`${cssClassName}__message-body`]}>
          {isThinking && hasTools && (
            <ThinkingInline label={texts.thinkingBubbleText} withTools />
          )}
          {hasTools && (
            <ToolActivitySection
              tools={displayTools}
              isStreaming={Boolean(isThinking)}
            />
          )}
          {content &&
            (isHtml ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(content),
                }}
              />
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
              >
                {content}
              </ReactMarkdown>
            ))}
          {isThinking && !hasTools && (
            <ThinkingInline label={texts.thinkingBubbleText} />
          )}
        </div>
        {fileAttachments.map((attachment) => (
          <button
            key={attachment.id}
            type="button"
            className={styles[`${cssClassName}__attachment`]}
            onClick={() => void handleAttachmentDownload(attachment)}
          >
            <img src={attachIcon} alt="" aria-hidden="true" />
            <span>{attachment.file_name}</span>
          </button>
        ))}

        {imageAttachments.length > 0 && (
          <div className={styles[`${cssClassName}__message-images`]}>
            {imageAttachments.map((attachment) => (
              <button
                key={attachment.id}
                type="button"
                title={attachment.file_name}
                className={styles[`${cssClassName}__message-image-button`]}
                onClick={() => void handleAttachmentDownload(attachment)}
              >
                {attachment.local_file ? (
                  <LocalAttachmentImage
                    file={attachment.local_file}
                    className={styles[`${cssClassName}__message-image`]}
                    alt={attachment.file_name}
                    title={attachment.file_name}
                  />
                ) : attachment.thumbnail_base64 ? (
                  <img
                    className={styles[`${cssClassName}__message-image`]}
                    src={`data:image/jpeg;base64,${attachment.thumbnail_base64}`}
                    alt={attachment.file_name}
                    title={attachment.file_name}
                  />
                ) : (
                  <AuthImage
                    className={styles[`${cssClassName}__message-image`]}
                    src={attachment.content_url}
                    baseUrl={supportUrlEnvVar()}
                    alt={attachment.file_name}
                    title={attachment.file_name}
                  />
                )}
              </button>
            ))}
          </div>
        )}

        {canGiveFeedback && (
          <div className={styles[`${cssClassName}__feedback-actions`]}>
            <button
              type="button"
              aria-label="Thumbs down"
              aria-pressed={useful === false}
              className={styles[`${cssClassName}__feedback-button`]}
              disabled={isFeedbackSubmitting}
              onClick={() => void handleFeedback(false)}
            >
              <img
                src={
                  useful === false ? thumbsDownRedIcon : thumbsDownOutlineIcon
                }
                alt=""
              />
            </button>
            <button
              type="button"
              aria-label="Thumbs up"
              aria-pressed={useful === true}
              className={styles[`${cssClassName}__feedback-button`]}
              disabled={isFeedbackSubmitting}
              onClick={() => void handleFeedback(true)}
            >
              <img
                src={useful === true ? thumbsUpGreenIcon : thumbsUpOutlineIcon}
                alt=""
              />
            </button>
          </div>
        )}
        {hasTimestamp && (
          <p className={styles[`${cssClassName}__timestamp`]}>
            <TimezoneDate date={comment.created_at} showRelative />
          </p>
        )}
      </div>
      <div className={styles[`${cssClassName}__message-avatar`]}>
        {comment.is_ai_chatbot_comment !== true && comment.author?.name ? (
          <InitialsAvatar name={comment.author.name} />
        ) : (
          <img
            src={isCurrentUserSameAsChatUser ? userIconBlue : chatBotProfile}
            alt={comment.author?.name}
          />
        )}
      </div>
    </div>
  );
};

export const ChatMessage = React.memo(ChatMessageComponent);
